import { EmberRosterRole, Entity, SIGIL_FLAG } from "game-common/models"
import type { ClientRenderable } from "./entity_rendering_manager"
import { entityAppearanceKey, updateEntityArtwork } from "./entity_artwork"
import { ACTIVE_FLOOR_FEATURES, FloorRenderFeatures } from "./floor_render_features"

interface VisualContext {
    role?: EmberRosterRole | null
    level?: number
    playerId?: string
}

interface VisualState {
    name: string; color: number; scale: number; submerged?: boolean; invading?: boolean
    appearance: string; hp: number; maxHp: number; speed: number; maxSpeed: number
    joinedTeam: boolean; sigil?: string; afk?: boolean; level: number; role: EmberRosterRole | null
    leader: boolean; spriteId?: string; expirationTs?: number; quantity?: number; landedArrow?: boolean
    landedArrowPitch?: number; landedArrowHeading?: number
    nameplates: boolean; healthBars: boolean
}

// A retained sprite can move between controllers; its last visual state must move with it.
const states = new WeakMap<ClientRenderable, Partial<VisualState>>()

/** One visual update path for current, projected and observed entities. No game/HUD side effects. */
export class EntityVisuals {
    updateRole(sprite: ClientRenderable, role: EmberRosterRole | null): void {
        const state = states.get(sprite)
        if (!state || state.role !== role) sprite.updateRole?.(role)
        if (state) state.role = role
    }

    update(sprite: ClientRenderable, entity: Entity, features: Readonly<FloorRenderFeatures> = ACTIVE_FLOOR_FEATURES,
        context: VisualContext = {}): void {
        const previous = states.get(sprite) ?? {}
        const next = {
            name: features.nameplates ? entity.name || "" : "",
            color: entity.color ?? 0xffffff,
            scale: entity.spriteScale ?? 1,
            submerged: entity.submerged,
            invading: entity.invading,
            appearance: entityAppearanceKey(entity),
            hp: entity.hp, maxHp: entity.maxHp,
            speed: entity.speed, maxSpeed: entity.maxSpeed,
            joinedTeam: entity.joinedTeam,
            sigil: entity.flags?.[SIGIL_FLAG], afk: entity.afk,
            level: features.levelLabels ? context.level ?? entity.level ?? 1 : 0,
            role: context.role === undefined ? previous.role : context.role,
            leader: features.leaderHalo && !!context.playerId && entity.leaderEntityId === context.playerId,
            spriteId: entity.spriteId, expirationTs: entity.expirationTs, quantity: entity.quantity, landedArrow: entity.landedArrow,
            landedArrowPitch: entity.landedArrowPitch,
            landedArrowHeading: entity.landedArrow ? entity.movement?.angle ?? 0 : undefined,
            nameplates: features.nameplates, healthBars: features.healthBars,
        }
        if (previous.name !== next.name) sprite.updateName?.(next.name)
        if (previous.color !== next.color) sprite.recolor?.(next.color)
        if (previous.scale !== next.scale) sprite.updateScale?.(next.scale)
        updateEntityArtwork(sprite, entity, previous)
        if (previous.submerged !== next.submerged) sprite.updateSubmerged?.(next.submerged)
        if (previous.invading !== next.invading) sprite.updatePlayerRing?.(entity)
        if (previous.appearance !== next.appearance) sprite.updatePaperdoll?.(entity)
        if (features.healthBars && (previous.hp !== next.hp || previous.maxHp !== next.maxHp || !previous.healthBars))
            sprite.update?.(entity.hp, entity.maxHp)
        else if (!features.healthBars && previous.healthBars) sprite.update?.(0, 0)
        if (previous.speed !== next.speed || previous.maxSpeed !== next.maxSpeed)
            sprite.updateSpeed?.(entity.maxSpeed > 0 ? entity.speed / entity.maxSpeed : 1)
        if (previous.joinedTeam !== next.joinedTeam) {
            if (next.joinedTeam) sprite.addTag?.()
            else sprite.removeTag?.()
        }
        if (previous.sigil !== next.sigil) sprite.updateSigil?.(next.sigil)
        if (previous.afk !== next.afk) sprite.updateAfk?.(next.afk)
        if (previous.level !== next.level) sprite.updateLevel?.(next.level)
        if (previous.role !== next.role) sprite.updateRole?.(next.role)
        if (previous.leader !== next.leader) sprite.updateHalo?.(entity, next.leader)
        states.set(sprite, next)
    }
}
